import React from 'react';
import { X } from 'lucide-react';
import SmartImage from './SmartImage';

interface ImageViewerModalProps {
    // Renders nothing when null — callers just keep this mounted and pass the URL to view.
    url: string | null;
    onClose: () => void;
    title?: string;
    // Most callers open this straight off a page, where the default z-[2000] is already
    // above everything. A caller that triggers it from inside another overlay (e.g. a
    // z-[9999] modal) needs to raise this so the viewer doesn't render underneath it.
    zIndexClassName?: string;
}

// Full-screen in-app popup for viewing a single uploaded document — used wherever an admin
// clicks a thumbnail to see it larger, instead of opening the raw image URL in a new tab.
const ImageViewerModal: React.FC<ImageViewerModalProps> = ({ url, onClose, title, zIndexClassName = 'z-[2000]' }) => {
    if (!url) return null;

    return (
        <div
            className={`fixed inset-0 ${zIndexClassName} flex items-center justify-center bg-black/70 backdrop-blur-sm p-4`}
            onClick={onClose}
        >
            <div className="relative max-w-4xl w-full max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
                <button
                    type="button"
                    onClick={onClose}
                    title="Close"
                    className="absolute -top-10 right-0 p-1.5 text-white/80 hover:text-white rounded-full hover:bg-white/10 transition-colors"
                >
                    <X size={22} />
                </button>
                {title && <p className="text-white/90 text-sm font-semibold mb-2 text-center">{title}</p>}
                <div className="bg-white rounded-xl overflow-hidden flex items-center justify-center p-2 flex-1 min-h-0">
                    <SmartImage
                        src={url}
                        alt={title || 'Document'}
                        showSpinner
                        containerClassName="w-full h-full flex items-center justify-center"
                        className="max-w-full max-h-[80vh] object-contain rounded-lg"
                    />
                </div>
            </div>
        </div>
    );
};

export default ImageViewerModal;
